import { jsx as _jsx } from "react/jsx-runtime";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip, } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { colors } from "healthex-common/colors";
import { sum, withoutUndefinedProperties } from "healthex-common/utils";
import { observer } from "mobx-react";
import { Bar, Line, Pie } from "react-chartjs-2";
import { appFonts } from "./AppView.styles";
import { ChartJSLineChartWrapper } from "./ChartJSCharts.styles";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement);
function toChartJSData(chartData) {
    const result = [];
    chartData[0].forEach((_dataElement) => result.push([]));
    chartData.forEach((innerData) => innerData.forEach((dataElement, dataElementIndex) => {
        if (typeof dataElement === "object") {
            result[dataElementIndex].push(dataElement.label);
        }
        else {
            result[dataElementIndex].push(dataElement);
        }
    }));
    return result;
}
export const ChartJSLineChart = observer((props) => {
    const chartJSData = toChartJSData(props.chartData);
    const yAxisCustomProperties = {
        border: {
            color: "transparent",
        },
        ticks: {
            font: {
                family: appFonts.join(","),
                size: 16,
            },
        },
        min: 0,
    };
    return (_jsx(ChartJSLineChartWrapper, { className: props.className, children: _jsx(Line, { data: {
                labels: chartJSData[0].slice(1),
                datasets: chartJSData
                    .slice(1)
                    .map((dataset, index) => ({
                    label: dataset[0].toString(),
                    data: dataset.slice(1),
                    borderColor: props.colors[index],
                    yAxisID: props.axisPerDataSet ? `y${index}` : undefined,
                }))
                    .reverse(),
            }, options: {
                responsive: true,
                maintainAspectRatio: false,
                elements: {
                    point: {
                        pointStyle: false,
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    title: {
                        display: false,
                    },
                },
                scales: withoutUndefinedProperties({
                    x: {
                        grid: {
                            color: "transparent",
                        },
                        ticks: {
                            minRotation: 30,
                            font: {
                                family: appFonts.join(","),
                                size: 12,
                            },
                        },
                    },
                    y: !props.axisPerDataSet
                        ? {
                            ...yAxisCustomProperties,
                        }
                        : undefined,
                    y0: props.axisPerDataSet
                        ? {
                            ...yAxisCustomProperties,
                        }
                        : undefined,
                    y1: props.axisPerDataSet
                        ? {
                            ...yAxisCustomProperties,
                            position: "right",
                        }
                        : undefined,
                }),
            } }) }));
});
export const ChartJSPieChart = observer((props) => {
    var _a, _b, _c, _d;
    const total = sum(Object.values(props.chartData));
    return (_jsx("div", { className: props.className, children: _jsx(Pie, { data: {
                labels: Object.keys(props.chartData),
                datasets: [
                    {
                        data: Object.values(props.chartData),
                        backgroundColor: props.colors,
                    },
                ],
            }, plugins: [ChartDataLabels], options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: props.labelsOutside
                        ? {
                            left: (_a = props.maxLabelWidth) !== null && _a !== void 0 ? _a : 150,
                            right: (_b = props.maxLabelWidth) !== null && _b !== void 0 ? _b : 150,
                            top: (_c = props.maxLabelHeight) !== null && _c !== void 0 ? _c : 100,
                            bottom: (_d = props.maxLabelHeight) !== null && _d !== void 0 ? _d : 100,
                        }
                        : undefined,
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    datalabels: {
                        formatter: (value, context) => {
                            var _a;
                            const label = String((_a = context.chart.data.labels) === null || _a === void 0 ? void 0 : _a[context.dataIndex]);
                            const percentage = `${Math.floor((value / total) * 100)}%`;
                            if (props.labelFormatter) {
                                return props.labelFormatter(value, percentage, label, context);
                            }
                            else if (props.labelType === "value") {
                                return `${label}: ${value}`;
                            }
                            else if (props.labelType === "percentage-and-value") {
                                return `${label}: ${percentage} (${value})`;
                            }
                            else {
                                return `${label}: ${percentage}`;
                            }
                        },
                        display: (context) => {
                            const value = context.dataset.data[context.dataIndex];
                            if (typeof value === "number")
                                return value > 0;
                            return true;
                        },
                        font: {
                            family: appFonts.join(","),
                            size: 10,
                        },
                        ...(props.labelsOutside
                            ? {
                                backgroundColor: (context) => props.colors[context.dataIndex % props.colors.length],
                                borderRadius: 4,
                                padding: 4,
                                anchor: "end",
                                align: "end",
                                offset: 10,
                            }
                            : undefined),
                        color: colors.black,
                    },
                },
            } }) }));
});
export const ChartJSBarChart = observer((props) => {
    const indexAxisProperties = {
        grid: {
            color: "transparent",
        },
        ticks: {
            font: {
                family: appFonts.join(","),
                size: 12,
            },
            color: colors.black,
            display: !props.hideIndexLabels,
        },
        border: {
            color: props.hideValueLabels ? "transparent" : colors.black,
        },
    };
    const valueAxisProperties = withoutUndefinedProperties({
        grid: props.hideValueLabels
            ? {
                color: "transparent",
            }
            : undefined,
        ticks: {
            font: {
                family: appFonts.join(","),
                size: 12,
            },
            color: colors.black,
            display: !props.hideValueLabels,
        },
        border: {
            color: "transparent",
        },
    });
    return (_jsx("div", { className: props.className, children: _jsx(Bar, { data: {
                labels: Object.keys(props.chartData),
                datasets: [
                    {
                        data: Object.values(props.chartData),
                        backgroundColor: props.colors,
                    },
                ],
            }, options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: props.orientation === "horizontal" ? "y" : "x",
                plugins: {
                    legend: {
                        display: false,
                    },
                },
                datasets: {
                    bar: {
                        maxBarThickness: 80,
                    },
                },
                scales: {
                    y: props.orientation === "horizontal" ? indexAxisProperties : valueAxisProperties,
                    x: props.orientation === "vertical" ? indexAxisProperties : valueAxisProperties,
                },
            } }) }));
});
